import { useState } from 'react';
import { useThemeTraffic, prettyPageLabel, formatDuration } from '@/hooks/useThemeTraffic';

type Period = 7 | 30;

/**
 * Trafic détaillé du site public : compteur de visites par page, visiteurs
 * uniques (IP), temps passé, produits les plus consultés et comportement
 * récent des visiteurs. Réservé aux données de la boutique connectée.
 */
export default function TrafficByPage() {
  const [period, setPeriod] = useState<Period>(7);
  const { data, loading, error, reload } = useThemeTraffic(period);

  return (
    <div className="space-y-4 md:gap-6 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-foreground-700 flex items-center gap-2">
            <i className="ri-radar-line text-primary-500"></i>
            Trafic de votre site par page
          </h3>
          <p className="text-xs text-foreground-400 mt-0.5">
            Visites, visiteurs uniques (IP) et temps passé sur chaque page de votre boutique
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={reload}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-background-50 border border-background-200/70 text-foreground-500 hover:text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
            title="Rafraîchir"
          >
            <i className={`ri-refresh-line text-sm ${loading ? 'animate-spin' : ''}`}></i>
          </button>
          <div className="flex items-center gap-0.5 bg-background-100 rounded-full p-0.5">
            {([7, 30] as Period[]).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors cursor-pointer ${
                  period === p ? 'bg-background-50 text-foreground-900 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'
                }`}
              >
                {p} jours
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="flex items-center gap-2 text-xs text-foreground-500 mb-1">
            <i className="ri-eye-line text-primary-500"></i> Visites de pages
          </div>
          <div className="text-2xl font-bold font-heading text-foreground-950">{data.totalViews}</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="flex items-center gap-2 text-xs text-foreground-500 mb-1">
            <i className="ri-group-line text-accent-500"></i> Visiteurs uniques (IP)
          </div>
          <div className="text-2xl font-bold font-heading text-foreground-950">{data.uniqueVisitors}</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <div className="flex items-center gap-2 text-xs text-foreground-500 mb-1">
            <i className="ri-timer-line text-secondary-600"></i> Temps moyen / page
          </div>
          <div className="text-2xl font-bold font-heading text-foreground-950">{formatDuration(data.avgTime)}</div>
        </div>
      </div>

      {error && (
        <div className="flex items-center justify-between bg-red-50 text-red-600 rounded-lg px-4 py-3 text-sm">
          <span>{error}</span>
          <button onClick={reload} className="font-medium underline cursor-pointer">Réessayer</button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
        {/* Pages les plus visitées */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <h4 className="text-sm font-semibold text-foreground-700 mb-4 flex items-center gap-2">
            <i className="ri-pages-line text-primary-500"></i> Pages les plus visitées
          </h4>
          {loading ? (
            <div className="flex items-center justify-center py-8"><i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i></div>
          ) : data.pages.length === 0 ? (
            <p className="text-sm text-foreground-400 py-4 text-center">Aucune visite enregistrée pour cette période</p>
          ) : (
            <div className="space-y-3">
              {data.pages.slice(0, 8).map((p) => {
                const max = data.pages[0]?.views || 1;
                return (
                  <div key={p.pageKey} className="flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-foreground-900 truncate">{prettyPageLabel(p.pageKey, null)}</span>
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{p.views} vues</span>
                      </div>
                      <div className="h-1.5 bg-background-200/70 rounded-full mt-1 overflow-hidden">
                        <div className="h-full bg-primary-400 rounded-full" style={{ width: `${(p.views / max) * 100}%` }} />
                      </div>
                      <div className="flex items-center gap-3 mt-1 text-[11px] text-foreground-400">
                        <span><i className="ri-group-line mr-0.5"></i>{p.uniqueVisitors} visiteurs</span>
                        <span><i className="ri-timer-line mr-0.5"></i>{formatDuration(p.avgTime)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Produits les plus consultés */}
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
          <h4 className="text-sm font-semibold text-foreground-700 mb-4 flex items-center gap-2">
            <i className="ri-shopping-bag-3-line text-accent-500"></i> Produits les plus consultés
          </h4>
          {loading ? (
            <div className="flex items-center justify-center py-8"><i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i></div>
          ) : data.topProducts.length === 0 ? (
            <p className="text-sm text-foreground-400 py-4 text-center">Aucune consultation de produit enregistrée</p>
          ) : (
            <div className="space-y-3">
              {data.topProducts.map((p, i) => {
                const max = data.topProducts[0]?.views || 1;
                return (
                  <div key={p.productId} className="flex items-center gap-3">
                    <span className="w-6 h-6 rounded-full bg-accent-100 flex items-center justify-center text-xs font-bold text-accent-700 flex-shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm text-foreground-900 truncate">{p.name}</span>
                        <span className="text-xs text-foreground-500 whitespace-nowrap">{p.views} vues</span>
                      </div>
                      <div className="h-1.5 bg-background-200/70 rounded-full mt-1 overflow-hidden">
                        <div className="h-full bg-accent-400 rounded-full" style={{ width: `${(p.views / max) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Comportement récent */}
      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
        <h4 className="text-sm font-semibold text-foreground-700 mb-4 flex items-center gap-2">
          <i className="ri-history-line text-secondary-600"></i> Comportement récent des visiteurs
        </h4>
        {loading ? (
          <div className="flex items-center justify-center py-8"><i className="ri-loader-4-line animate-spin text-xl text-primary-500"></i></div>
        ) : data.behavior.length === 0 ? (
          <p className="text-sm text-foreground-400 py-4 text-center">Aucun comportement enregistré</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-foreground-400 uppercase tracking-wider border-b border-background-200/70">
                  <th className="py-2 pr-4 font-semibold">Date</th>
                  <th className="py-2 pr-4 font-semibold">Page</th>
                  <th className="py-2 pr-4 font-semibold">Événement</th>
                  <th className="py-2 pr-4 font-semibold">Durée</th>
                  <th className="py-2 font-semibold">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/70">
                {data.behavior.map((b) => (
                  <tr key={b.id} className="hover:bg-background-50/60">
                    <td className="py-2 pr-4 text-foreground-500 whitespace-nowrap">
                      {new Date(b.at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="py-2 pr-4 text-foreground-800 truncate max-w-[180px]">{prettyPageLabel(b.pageKey, b.path)}</td>
                    <td className="py-2 pr-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        b.event === 'view' ? 'bg-primary-50 text-primary-700'
                          : b.event === 'leave' ? 'bg-accent-50 text-accent-700'
                          : 'bg-secondary-50 text-secondary-700'
                      }`}>
                        {b.event === 'view' ? 'Vue' : b.event === 'leave' ? 'Sortie' : 'Ping'}
                      </span>
                    </td>
                    <td className="py-2 pr-4 text-foreground-500 whitespace-nowrap">{formatDuration(b.duration)}</td>
                    <td className="py-2 text-foreground-400 font-mono text-xs truncate max-w-[140px]">{b.ip || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}